import { useEffect, useState } from 'react';
import { generatePath } from 'react-router';

import { RiskRegisterApi } from 'Api/RiskRegister/RiskRegisterApi';
import { Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FormFieldMultiOptionSelect } from 'Components/FormField/FormFieldMultiOptionSelect/FormFieldMultiOptionSelect';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { PageLayoutHybridDashboard } from 'Components/PageLayout/PageLayoutHybridDashboard';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { RiskCallout } from 'Components/RiskRegister/RiskCallout/RiskCallout';
import { RiskTable } from 'Components/RiskRegister/RiskTable/RiskTable';
import { Text } from 'Components/Text/Text';
import { ICON_ADD_CREATE } from 'Config/Icons';
import { CREATE_RISK } from 'Config/PathTemplates';
import { RISK_MANAGEMENT_CONTROL_EFFECTIVENESS, RISK_MANAGEMENT_TARGET_RESIDUAL_RISK } from 'Config/Tooltips';
import { ResponseModel } from 'Models/ResponseModel';
import { ALL_RISKS, RiskCategoryResponse, RiskResponse } from 'Models/RiskRegister';
import { GroupOptionType } from 'Models/Types/GlobalType';

import styles from './RiskListing.module.css';

export interface RiskListingProps {
    riskRegisterApi: RiskRegisterApi;
}

export const RiskListing = (props: RiskListingProps) => {
    const cachedData = useCachedData();

    const [risks, setRisks] = useState<RiskResponse[]>();
    const [riskCategories, setRiskCategories] = useState<RiskCategoryResponse[]>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [overallControlEffectiveness, setOverallControlEffectiveness] = useState<number>();
    const [error, setError] = useState<Error>();
    const [selectedTagsFilter, setSelectedTagsFilter] = useState<string[]>([]);
    const [selectedRiskCategory, setSelectedRiskCategory] = useState<string>(ALL_RISKS);

    useEffect(() => {
        const fetchOverallControlEffectiveness = async (): Promise<void> => {
            try {
                const effectivenessResponse = await props.riskRegisterApi.getOverallControlEffectiveness();
                setOverallControlEffectiveness(effectivenessResponse.data);
            } catch (error) {
                setError(error);
            }
        };

        fetchOverallControlEffectiveness();
    }, [props.riskRegisterApi]);

    useEffect(() => {
        const getRisks = async (): Promise<void> => {
            try {
                const riskResponse: ResponseModel<RiskResponse[]> = await props.riskRegisterApi.getAllRisks();
                setRisks(riskResponse.data);
            } catch (error) {
                setErrorMessage(error.message);
            }
        };
        getRisks();
    }, [props.riskRegisterApi]);

    useEffect(() => {
        const getRiskCategories = async (): Promise<void> => {
            try {
                const response = await props.riskRegisterApi.getRiskCategories();
                setRiskCategories([{ title: ALL_RISKS, id: ALL_RISKS, in_use: true }, ...response.data]);
            } catch (error) {
                setErrorMessage(error.message);
            }
        };
        getRiskCategories();
    }, [props.riskRegisterApi]);

    const handleSelectChange = (value: ChangeEventType): void => {
        setSelectedRiskCategory(value as string);
    };

    if (error) {
        return <Text>{error.message}</Text>;
    }

    if (risks && riskCategories && overallControlEffectiveness !== undefined) {
        const filteredRisks = risks.filter((risk) => selectedRiskCategory === ALL_RISKS || risk.category.id === selectedRiskCategory).filter((risk) => selectedTagsFilter.length === 0 || risk.tags.some((tag) => selectedTagsFilter.includes(tag)));

        return (
            <PageLayoutHybridDashboard
                headerTitle="Dynamic Risk Register"
                headerButtons={
                    <Link variant="primaryButton" to={generatePath(CREATE_RISK)} fontAwesomeImage={ICON_ADD_CREATE}>
                        Create Risk
                    </Link>
                }
                headerFilters={
                    <>
                        <div className={styles.filterContainer}>
                            <FormFieldSelect options={riskCategories.map((category) => ({ label: category.title, value: category.id }))} formFieldId="selectedRiskCategory" formFieldLabel="Filter By Risk Category" handleChange={handleSelectChange} selectedOption={selectedRiskCategory} colorTheme="dark" notClearable />
                        </div>
                        <div className={styles.filterContainer}>
                            <FormFieldMultiOptionSelect
                                defaultSelectedOptions={cachedData
                                    .getTagCategoriesGroupedOptions()
                                    .map((group) => group.options)
                                    .flat()
                                    .filter((option) => selectedTagsFilter.includes(option.value as string))}
                                formFieldLabel="Filter By Tags"
                                formFieldId="risk_tags"
                                handleChange={(value: GroupOptionType[]) => setSelectedTagsFilter(value.map((tag) => tag.value as string))}
                                options={cachedData.getTagCategoriesGroupedOptions()}
                                accessibilityLabel="tag selection"
                                colorTheme="dark"
                            />
                        </div>
                    </>
                }
                headerDashlets={[
                    {
                        dashlets: [
                            {
                                title: 'Inherent Risk',
                                content: <RiskCallout calloutType="total_inherent_risk" completeEnvironmentEffectivenessAverage={overallControlEffectiveness} risks={filteredRisks} />,
                            },
                            {
                                title: 'Control Effectiveness',
                                titleTooltip: RISK_MANAGEMENT_CONTROL_EFFECTIVENESS,
                                content: <RiskCallout calloutType="control_environment_effectiveness" completeEnvironmentEffectivenessAverage={overallControlEffectiveness} risks={filteredRisks} />,
                            },
                            {
                                title: 'Current Residual Risk',
                                content: <RiskCallout calloutType="total_current_risk" completeEnvironmentEffectivenessAverage={overallControlEffectiveness} risks={filteredRisks} />,
                            },
                            {
                                title: 'Target Residual Risk',
                                titleTooltip: RISK_MANAGEMENT_TARGET_RESIDUAL_RISK,
                                content: <RiskCallout calloutType="total_target_risk" completeEnvironmentEffectivenessAverage={overallControlEffectiveness} risks={filteredRisks} />,
                            },
                        ],
                    },
                ]}
                body={[
                    {
                        title: 'Risk Listing',
                        content: <RiskTable type="current risks" risks={filteredRisks} />,
                    },
                ]}
            />
        );
    } else if (errorMessage) {
        return <Text>{errorMessage}</Text>;
    } else {
        return <Placeholder />;
    }
};
